/**
 * NotificationBell - In-app notification system
 * Shows bell icon with unread badge, dropdown with notification list.
 * Currently handles marketplace ad expiration alerts.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useTranslation } from '../translations';

const API = process.env.REACT_APP_BACKEND_URL;

const NotificationIcon = ({ count }) => (
  <div className="relative cursor-pointer" data-testid="notification-bell">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
    {count > 0 && (
      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold" data-testid="notification-count">
        {count > 9 ? '9+' : count}
      </span>
    )}
  </div>
);

const NotificationItem = ({ notification, onRead, onNavigate, t }) => {
  const isExpiring = notification.type === 'ad_expiring';
  const isExpired = notification.type === 'ad_expired';
  const isContact = notification.type === 'contact_message';
  
  return (
    <div
      data-testid={`notification-item-${notification.id}`}
      className={`p-3 border-b last:border-b-0 cursor-pointer transition-colors ${
        notification.read ? 'bg-white' : 'bg-blue-50'
      } hover:bg-gray-50`}
      onClick={() => {
        if (!notification.read) onRead(notification.id);
        if (notification.reference_type === 'marketplace_item') onNavigate('marketplace');
        if (notification.reference_type === 'contact') onNavigate('messages');
      }}
    >
      <div className="flex items-start gap-2">
        <span className="text-lg mt-0.5">
          {isContact ? '✉️' : isExpired ? '🔴' : isExpiring ? '🟠' : '🔔'}
        </span>
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${notification.read ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
            {notification.message}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {formatTimeAgo(notification.created_at, t)}
          </p>
        </div>
        {!notification.read && (
          <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
        )}
      </div>
    </div>
  );
};

function formatTimeAgo(dateStr, t) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return t('notifNow');
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export const NotificationBell = ({ onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const { t } = useTranslation();

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/notifications`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  }, []);

  const checkExpiringAds = useCallback(async () => {
    try {
      await axios.post(`${API}/api/notifications/check-expiring-ads`);
      await fetchNotifications();
    } catch (err) {
      console.error('Error checking expiring ads:', err);
    }
  }, [fetchNotifications]);

  useEffect(() => {
    fetchNotifications();
    checkExpiringAds();
    // Poll every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications, checkExpiringAds]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/api/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleReadAll = async () => {
    try {
      await axios.patch(`${API}/api/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleNavigate = (tab) => {
    setIsOpen(false);
    if (onNavigate) onNavigate(tab);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div onClick={() => setIsOpen(!isOpen)}>
        <NotificationIcon count={unreadCount} />
      </div>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-sm text-gray-800">{t('notifTitle')}</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleReadAll}
                className="text-xs text-blue-600 hover:text-blue-800"
                data-testid="mark-all-read-btn"
              >
                {t('notifMarkAllRead')}
              </button>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto max-h-72">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">
                {t('notifEmpty')}
              </div>
            ) : (
              notifications.map(n => (
                <NotificationItem 
                  key={n.id} 
                  notification={n} 
                  onRead={handleRead}
                  onNavigate={handleNavigate}
                  t={t}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
